import useFormFeedback from "@/lib/useFormFeedback";
import { useRef } from 'react';
import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight, CheckCircle2 } from "lucide-react";
import { API } from "@/App";

const CONTACT_METHODS = ["Phone", "Text Message", "Email"];
const PICKUP_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
const SERVICE_LEVELS = ["One-time visit — $24", "Twice a month — $40/month", "Weekly — $50/month"];
const PLAN_PRICES = { [SERVICE_LEVELS[0]]: 24, [SERVICE_LEVELS[1]]: 40, [SERVICE_LEVELS[2]]: 50 };

export default function TrashCanValetPage({ embedded = false }) {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    serviceAddress: "",
    city: "",
    preferredContactMethod: "Text Message",
    serviceLevel: "",
    pickupDay: "",
    trashCans: "1",
    recycleCans: "1",
    yardDebrisCans: "1",
    gatedCommunity: false,
    gateCode: "",
    returnLocation: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { setError, fieldProps, fieldError, errorSummary } = useFormFeedback("valet");

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const counts = [form.trashCans, form.recycleCans, form.yardDebrisCans];
  const validCounts = counts.every(value => value !== '' && Number.isSafeInteger(Number(value)) && Number(value) >= 0);
  const totalCans = counts.reduce((total, value) => total + Number(value), 0);
  const extraCans = Math.max(0, totalCans - 3);
  const monthlyPlan = form.serviceLevel === SERVICE_LEVELS[1] || form.serviceLevel === SERVICE_LEVELS[2];
  const basePrice = PLAN_PRICES[form.serviceLevel];
  const needsOneTimeQuote = !monthlyPlan && extraCans > 0;
  const totalPrice = basePrice + (monthlyPlan ? extraCans * 8 : 0);

  const inquiryId = useRef(crypto.randomUUID());
  const submit = async () => {
    if (!form.fullName.trim()) return setError("Please enter your full name.", "valet-fullName");
    if (!form.email.trim()) return setError("Please enter your email address.", "valet-email");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return setError("Please enter a valid email address.", "valet-email");
    if (!form.phone.trim()) return setError("Please enter your phone number.", "valet-phone");
    if (form.phone.replace(/\D/g, "").length < 10) return setError("Please enter a valid phone number.", "valet-phone");
    if (!form.serviceAddress.trim()) return setError("Please enter the service address.", "valet-serviceAddress");
    if (!form.city.trim()) return setError("Please enter your city.", "valet-city");
    if (!form.serviceLevel) return setError("Please choose the level of service you want.", "valet-serviceLevel");
    if (!form.pickupDay) return setError("Please choose your trash pickup day.", "valet-pickupDay");
    if (!form.returnLocation.trim()) return setError("Please tell us where to return the cans after pickup.", "valet-returnLocation");

    for (const [key, label] of [['trashCans', 'trash cans'], ['recycleCans', 'recycling cans'], ['yardDebrisCans', 'yard-debris cans']]) {
      if (form[key] === '' || !Number.isSafeInteger(Number(form[key])) || Number(form[key]) < 0) {
        return setError(`Enter a whole number of 0 or more for ${label}.`, `valet-${key}`);
      }
    }
    if (!Number.isSafeInteger(totalCans) || totalCans < 1) return setError("Please enter at least one can to service.", "valet-trashCans");

    setSubmitting(true);
    setError("");

    try {
      const message = [
        "Trash Can Valet Signup Request",
        `Preferred contact method: ${form.preferredContactMethod}`,
        `Service level: ${form.serviceLevel}`,
        `Pickup day: ${form.pickupDay}`,
        `Service address: ${form.serviceAddress}`,
        `City: ${form.city}`,
        `Cans: trash ${form.trashCans}, recycle ${form.recycleCans}, yard debris ${form.yardDebrisCans}`,
        `Total cans: ${totalCans}; included allowance: 3 cans in any mix; extra cans: ${extraCans}`,
        monthlyPlan ? `Monthly pricing: $${basePrice} base + ${extraCans} extra cans × $8/month = $${totalPrice}/month` : needsOneTimeQuote ? `One-time pricing: $24 base; additional-can price requires confirmation (monthly surcharge does not apply).` : 'One-time pricing: $24',
        'All applicable collection days included; collection schedule to be confirmed.',
        `Gated community: ${form.gatedCommunity ? "Yes" : "No"}`,
        form.gateCode ? `Gate code / access details: ${form.gateCode}` : null,
        `Return location: ${form.returnLocation}`,
        form.notes ? `Special notes: ${form.notes}` : null,
      ].filter(Boolean).join("\n");

      const response = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          submission_id: inquiryId.current,
          full_name: form.fullName,
          email: form.email,
          phone: form.phone,
          service_needed: "Trash Can Valet",
          service_area: form.city,
          message,
        }),
      });

      if (!response.ok) {
        throw new Error("Could not submit your valet signup right now. Please call us directly.");
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {!embedded && <>
      <SEO
        title="Trash Can Valet Signup | Junk In Da Truck"
        description="Trash Can Valet: twice-monthly service $40/month or weekly $50/month includes 3 cans in any mix. Each additional can is $8/month. One-time service starts at $24."
        path="/trash-can-valet"
      />
      <Header />

      <section className="relative pt-24 sm:pt-28 bg-[#0D0D0D] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="/trash-can-valet.png" alt="Trash Can Valet" className="w-full h-full object-cover" />
        </div>
        <div className="relative mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center rounded-full bg-[#97BF04]/15 px-4 py-2 text-sm font-semibold text-[#97BF04] border border-[#97BF04]/20 mb-5">
              New Recurring Service
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              No More Dragging Cans To The Curb.
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 leading-relaxed max-w-2xl">
              Weekly Trash Can Valet service for busy households, seniors, rentals, and HOAs. We roll your cans out before pickup day and return them after collection, with support for trash, recycling, and yard debris setups.
            </p>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-white font-medium mt-6 bg-black/40 backdrop-blur-sm rounded-2xl border border-white/10 px-4 py-3 max-w-xl">
              <span>⭐ 5.0 Google rating</span>
              <span>Reliable weekly routine</span>
              <span>Trash, recycle, yard debris</span>
            </div>
          </div>
        </div>
      </section>
      </>}

      <section className="py-14 sm:py-16 bg-[#F8F8F4]">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1.05fr,0.95fr] gap-8 lg:gap-10">
          <div className="bg-white rounded-3xl border border-[#0D0D0D]/8 shadow-lg p-6 sm:p-8">
            {submitted ? (
              <div className="space-y-5">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#97BF04]/15 px-4 py-2 text-sm font-semibold text-[#2C4001]">
                  <CheckCircle2 className="w-4 h-4" /> Signup received
                </div>
                <h2 className="text-3xl font-semibold text-[#0D0D0D]" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  You are on the list.
                </h2>
                <p className="text-[#4B5563] leading-relaxed">
                  We got your Trash Can Valet request and we will follow up using your preferred contact method to confirm service details and next steps.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a href="tel:+17724945048"><Button className="bg-[#0D0D0D] text-white hover:bg-[#1a1a1a]"><Phone className="w-4 h-4 mr-2" /> Call Us</Button></a>
                  <a href="/services/trash-can-valet"><Button variant="outline">Back to Service Page</Button></a>
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-3xl font-semibold text-[#0D0D0D] mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Trash Can Valet Signup
                </h2>
                <p className="text-[#4B5563] mb-6 leading-relaxed">
                  Choose a one-time visit or a monthly plan. Every option includes the first 3 cans in any mix. Additional cans on monthly plans are $8 each per month. We will confirm your address, pickup-day details and start date before service begins.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="valet-fullName" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Full Name *</label>
                    <input className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("valet-fullName")} required aria-required="true" value={form.fullName} onChange={(e) => update('fullName', e.target.value)} placeholder="Your full name" />
                    {fieldError("valet-fullName")}
                  </div>
                  <div>
                    <label htmlFor="valet-email" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Email *</label>
                    <input type="email" className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("valet-email")} required aria-required="true" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@example.com" />
                    {fieldError("valet-email")}
                  </div>
                  <div>
                    <label htmlFor="valet-phone" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Phone *</label>
                    <input type="tel" className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("valet-phone")} required aria-required="true" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="Best number to reach you" />
                    {fieldError("valet-phone")}
                  </div>
                  <div>
                    <label htmlFor="valet-preferredContactMethod" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Preferred Contact Method *</label>
                    <select className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("valet-preferredContactMethod")} required aria-required="true" value={form.preferredContactMethod} onChange={(e) => update('preferredContactMethod', e.target.value)}>
                      {CONTACT_METHODS.map((method) => <option key={method}>{method}</option>)}
                    </select>
                    {fieldError("valet-preferredContactMethod")}
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="valet-serviceAddress" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Service Address *</label>
                    <input className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("valet-serviceAddress")} required aria-required="true" value={form.serviceAddress} onChange={(e) => update('serviceAddress', e.target.value)} placeholder="Street address" />
                    {fieldError("valet-serviceAddress")}
                  </div>
                  <div>
                    <label htmlFor="valet-city" className="block text-sm font-semibold text-[#0D0D0D] mb-2">City *</label>
                    <input className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("valet-city")} required aria-required="true" value={form.city} onChange={(e) => update('city', e.target.value)} placeholder="Vero Beach, Sebastian, etc." />
                    {fieldError("valet-city")}
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="valet-serviceLevel" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Level of Service *</label>
                    <select className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("valet-serviceLevel")} required aria-required="true" value={form.serviceLevel} onChange={(e) => update('serviceLevel', e.target.value)}>
                      <option value="">Select service level</option>
                      {SERVICE_LEVELS.map((level) => <option key={level}>{level}</option>)}
                    </select>
                    {fieldError("valet-serviceLevel")}
                    <p className="field-help">Twice monthly is $40/month; weekly is $50/month. Both include 3 cans in any mix, plus $8/month for each additional can. All applicable collection days are included. Tell us any separate recycling or yard collection days in Special Notes.</p>
                  </div>
                  <div>
                    <label htmlFor="valet-pickupDay" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Trash Pickup Day *</label>
                    <select className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("valet-pickupDay")} required aria-required="true" value={form.pickupDay} onChange={(e) => update('pickupDay', e.target.value)}>
                      <option value="">Select pickup day</option>
                      {PICKUP_DAYS.map((day) => <option key={day}>{day}</option>)}
                    </select>
                    {fieldError("valet-pickupDay")}
                  </div>
                  <div>
                    <label htmlFor="valet-trashCans" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Trash Cans</label>
                    <input type="number" min="0" className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("valet-trashCans")} value={form.trashCans} onChange={(e) => update('trashCans', e.target.value)} />
                    {fieldError("valet-trashCans")}
                  </div>
                  <div>
                    <label htmlFor="valet-recycleCans" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Recycle Cans</label>
                    <input type="number" min="0" className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("valet-recycleCans")} value={form.recycleCans} onChange={(e) => update('recycleCans', e.target.value)} />
                    {fieldError("valet-recycleCans")}
                  </div>
                  <div>
                    <label htmlFor="valet-yardDebrisCans" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Yard Debris Cans</label>
                    <input type="number" min="0" className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("valet-yardDebrisCans")} value={form.yardDebrisCans} onChange={(e) => update('yardDebrisCans', e.target.value)} />
                    {fieldError("valet-yardDebrisCans")}
                  </div>
                  <div className="sm:col-span-2">
                    <section className="valet-price-summary" aria-label="Valet price summary" aria-live="polite" aria-atomic="true">
                      <h3>Your valet plan</h3>
                      {!validCounts || !Number.isSafeInteger(totalCans) || totalCans < 1 ? <p>Enter your can counts to see your price.</p> : <>
                        <p>{totalCans} total {totalCans === 1 ? 'can' : 'cans'} · First 3 included in any mix</p>
                        {!basePrice ? <p>Choose a service level to see your price.</p> : <>
                          <dl>
                            <div><dt>Base service</dt><dd>${basePrice}{monthlyPlan ? '/month' : ''}</dd></div>
                            <div><dt>Extra cans ({extraCans})</dt><dd>{monthlyPlan ? `$${extraCans * 8}/month` : extraCans ? 'Price to be confirmed' : '$0'}</dd></div>
                            <div className="valet-price-total"><dt>{needsOneTimeQuote ? 'One-time estimate' : monthlyPlan ? 'Monthly total' : 'One-time total'}</dt><dd>{needsOneTimeQuote ? 'Custom quote' : `$${totalPrice}${monthlyPlan ? '/month' : ''}`}</dd></div>
                          </dl>
                          {needsOneTimeQuote && <p>The $8 extra-can rate applies to monthly plans. Our crew will confirm the one-time price for more than 3 cans.</p>}
                          <p>Roll-out and return included. We confirm your collection schedule before service begins.</p>
                        </>}
                      </>}
                    </section>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="inline-flex items-center gap-3 text-sm text-[#0D0D0D] font-medium">
                      <input type="checkbox" checked={form.gatedCommunity} onChange={(e) => update('gatedCommunity', e.target.checked)} />
                      This property is in a gated community or needs access instructions.
                    </label>
                  </div>
                  {form.gatedCommunity && (
                    <div className="sm:col-span-2">
                      <label htmlFor="valet-gateCode" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Gate Code / Access Details</label>
                      <input className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("valet-gateCode")} value={form.gateCode} onChange={(e) => update('gateCode', e.target.value)} placeholder="Gate code, keypad notes, call box, etc." />
                    {fieldError("valet-gateCode")}
                    </div>
                  )}
                  <div className="sm:col-span-2">
                    <label htmlFor="valet-returnLocation" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Where should we return the cans after pickup? *</label>
                    <input className="w-full rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("valet-returnLocation")} required aria-required="true" value={form.returnLocation} onChange={(e) => update('returnLocation', e.target.value)} placeholder="By the garage, side gate, fence line, etc." />
                    {fieldError("valet-returnLocation")}
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="valet-notes" className="block text-sm font-semibold text-[#0D0D0D] mb-2">Special Notes</label>
                    <textarea className="w-full min-h-[120px] rounded-xl border border-[#0D0D0D]/12 bg-white px-4 py-3" {...fieldProps("valet-notes")} value={form.notes} onChange={(e) => update('notes', e.target.value)} placeholder="Anything else we should know about your cans, route, or property?" />
                    {fieldError("valet-notes")}
                  </div>
                </div>

                {errorSummary}

                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <Button onClick={submit} disabled={submitting} className="bg-[#97BF04] text-[#0D0D0D] hover:bg-[#83A603] font-semibold gap-2">
                    {submitting ? 'Submitting...' : 'Request Trash Can Valet Signup'} <ArrowRight className="w-4 h-4" />
                  </Button>
                  <a href="tel:+17724945048"><Button variant="outline" className="font-semibold gap-2"><Phone className="w-4 h-4" /> Call (772) 494-5048</Button></a>
                </div>
              </>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-3xl border border-[#0D0D0D]/8 shadow-lg p-6 sm:p-8">
              <h3 className="text-2xl font-semibold text-[#0D0D0D] mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                What is included
              </h3>
              <ul className="space-y-3 text-[#4B5563] leading-relaxed">
                <li>• First 3 cans included in any mix</li>
                <li>• Each additional can is $8/month on recurring plans</li>
                <li>• All applicable collection days included</li>
                <li>• Roll cans out on your confirmed service days</li>
                <li>• Return cans after collection</li>
                <li>• Handle trash, recycle, and yard debris setups</li>
                <li>• Built around Indian River County universal curbside collection</li>
                <li>• Great for seniors, busy families, rentals, and HOAs</li>
              </ul>
            </div>

            <div className="bg-white rounded-3xl border border-[#0D0D0D]/8 shadow-lg p-6 sm:p-8">
              <h3 className="text-2xl font-semibold text-[#0D0D0D] mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Indian River County service notes
              </h3>
              <div className="space-y-3 text-[#4B5563] leading-relaxed">
                <p>
                  Unincorporated Indian River County moved to universal curbside collection on October 1, 2025. County materials describe service for carted trash, recycling, and yard waste.
                </p>
                <p>
                  The county also points residents to a pickup-day lookup tool, and Waste Management publishes the collection-service brochure and service-area map used for county service guidance.
                </p>
              </div>
              <div className="flex flex-col gap-3 mt-5">
                <a href="https://gisportal.ircgov.com/irc-swdd/garbage-recycle.html" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-[#97BF04] text-[#0D0D0D] hover:bg-[#83A603] font-semibold">Check Your Pickup Day</Button>
                </a>
                <a href="https://www.wm.com/content/dam/wm/local-pages/florida/indian-river-county/IRC_Collection_Service_Guidelines_Brochure_2025.pdf" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full font-semibold">View WM Service Guidelines & Map</Button>
                </a>
                <a href="https://www.indianriver.gov/services/solid_waste_disposal_district/index.php" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full font-semibold">View County Solid Waste Info</Button>
                </a>
              </div>
            </div>
            <div className="bg-[#0D0D0D] rounded-3xl text-white p-6 sm:p-8">
              <h3 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Also need junk hauled away?
              </h3>
              <p className="text-gray-300 leading-relaxed mb-4">
                If you also need junk hauled away, use the regular estimate form separately.
              </p>
              <a href="/estimate" className="inline-flex items-center gap-2 text-[#97BF04] font-semibold hover:text-[#b7e21a] transition-colors">
                Go to Estimate Form <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {!embedded && <Footer />}
    </div>
  );
}
